<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :is-focused="control.isFocused.value"
    :applied-options="control.appliedOptions.value"
  >
    <SectionWrapper id="license-plate-confirm" class="flex flex-col">
      <template #header>
        <div class="flex w-full items-center justify-between" :class="control.styles.control.label">
          <h3>
            {{ control.computedLabel.value }}
          </h3>
          <Transition name="slide-up">
            <WButton
              v-if="isLicensePlateConfirmed"
              type="ghost"
              :disabled="!control.control.value.enabled"
              :config="getClientTheme()"
              @click="isLicensePlateConfirmed = false"
            >
              <WidgetIcon :icon="vehicleOptions.editIcon" height="1.5rem" class="widget-action-icon" />
            </WButton>
          </Transition>
        </div>
      </template>
      <div class="flex w-full flex-col">
        <HeightTransition :visible="isLicensePlateConfirmed">
          {{ isOtherPlate ? newLicensePlate : selectedLicensePlate }}
        </HeightTransition>
        <HeightTransition :visible="!isLicensePlateConfirmed">
          <div class="widget-base-radio-group">
            <legend class="sr-only">{{ control.computedLabel }}</legend>
            <div
              v-if="licensePlates.length"
              :class="twMerge('widget-base-radio-group_wrapper', control.styles.control.input)"
              :disabled="!control.control.value.enabled"
              :autofocus="control.appliedOptions.value.focus"
              :placeholder="control.appliedOptions.value.placeholder"
              :hint="control.control.value.description"
              :persistent-hint="control.persistentHint()"
              :required="control.control.value.required"
            >
              <div
                v-for="option in licensePlates"
                :key="option"
                class="widget-base-radio-group_input-wrapper"
                @click="onChange(option)"
              >
                <WInput
                  :disabled="!control.control.value.enabled"
                  :id="String(option)"
                  :name="control.computedLabel.value.toLowerCase().replaceAll(' ', '-')"
                  type="radio"
                  :checked="option === selectedLicensePlate"
                  class="widget-base-radio-group_input"
                  @input="onChange(option)"
                />
                <label :for="option" class="widget-base-radio-group_label">{{ option }}</label>
              </div>
            </div>
          </div>
        </HeightTransition>
        <HeightTransition
          :visible="isOtherPlate && !isLicensePlateConfirmed"
          :class="{ 'mt-6': !!licensePlates.length }"
        >
          <WInput
            v-model="newLicensePlate"
            type="text"
            name="licensePlate"
            :error="
              licensePlateError ||
              control.control.value.errors?.replace('is a required property', 'This field is required') ||
              ''
            "
            :label="inputLabel"
            @update:model-value="submitPlate"
          />
        </HeightTransition>
      </div>
    </SectionWrapper>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { ControlElement } from '@jsonforms/core';
  import { computed, nextTick, ref, watch } from 'vue';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import SectionWrapper from 'components/claims-widget/components/SectionWrapper.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import { config, getClientTheme } from 'src/utils/config';
  import { twMerge } from 'tailwind-merge';
  import WInput from '../../base/Input.vue';
  import WButton from '../../base/Button.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import HeightTransition from 'src/components/base/HeightTransition.vue';
  import { useDebounceFn } from '@vueuse/core';

  interface VehicleOptions {
    editIcon?: string;
    deleteIcon?: string;
    confirm?: string;
    noOp?: string;
    label?: string;
  }
  const props = defineProps(rendererProps<ControlElement>());

  const control = useWidgetControl(useJsonFormsControl(props));
  const vehicleOptions = ref(
    (control.control.value.schema as unknown as { licensePlateSection: VehicleOptions }).licensePlateSection
  );
  const licensePlateError = ref('');
  const otherOption = vehicleOptions.value.noOp || 'Other';
  const licensePlates = computed(() => {
    let plates: string[] = [];
    if (!config.value.policy) {
      if (config.value.vehicle?.licensePlate) {
        plates = [config.value.vehicle.licensePlate];
      } else {
        plates = [];
      }
    } else if (Array.isArray(config.value.policy)) {
      plates = config.value.policy
        .map((policy) => policy.vehicle?.licensePlate)
        .filter(isString)
        .reduce((acc, current) => {
          if (!acc.includes(current)) {
            acc.push(current);
          }
          return acc;
        }, [] as string[]);
    } else {
      plates = [config.value.policy.vehicle?.licensePlate].filter(isString);
    }
    if (plates.length !== 0) {
      plates.push(otherOption);
    }
    return plates;
  });
  const inputLabel = computed(() =>
    vehicleOptions.value.label ? `${licensePlates.value?.length ? 'Other ' : ''} ${vehicleOptions.value.label}` : ''
  );

  const newLicensePlate = ref('');
  const isOtherPlate = ref(!licensePlates.value.length);

  const selectedLicensePlate = ref(control.control.value.data || licensePlates.value[0]);
  const isLicensePlateConfirmed = ref(!!control.control.value.data || false);
  // infer and set the other plate correctly if previously set
  if (control.control.value.data) {
    const newLicense = licensePlates.value.find((plate) => plate === control.control.value.data);
    if (!newLicense) {
      selectedLicensePlate.value = otherOption;
      newLicensePlate.value = control.control.value.data;
      isOtherPlate.value = true;
    }
  }

  const submitLicensePlate = useDebounceFn(() => {
    if (newLicensePlate.value && isOtherPlate.value) {
      control.onChange(newLicensePlate.value);
    } else {
      control.onChange(selectedLicensePlate.value);
    }
  }, 500);

  submitLicensePlate();

  function submitPlate(plate: string) {
    nextTick(() => {
      newLicensePlate.value = plate.toUpperCase().trim();
      const rgx = new RegExp('[^a-zA-Z0-9]', 'g');
      newLicensePlate.value = String(newLicensePlate.value).replaceAll(rgx, '');

      if (plate.length > 7) {
        newLicensePlate.value = plate.slice(0, 7);
      }
      if (/^[A-Za-z0-9]*$/.test(newLicensePlate.value)) {
        licensePlateError.value = '';
        submitLicensePlate();
        return;
      }

      licensePlateError.value = 'License plate must contains only alphanumeric characters and numbers';
    });
  }

  if (selectedLicensePlate.value) {
    submitPlate(newLicensePlate.value);
  }
  // set in confirmed mode if only one license plate
  if (licensePlates.value.length === 2 && !isOtherPlate.value) {
    selectedLicensePlate.value = licensePlates.value[0];
    isLicensePlateConfirmed.value = true;
    submitPlate(newLicensePlate.value);
  }

  function isString(val: string | undefined): val is string {
    if (typeof val === 'string') return true;
    return false;
  }

  function onChange(val: string) {
    selectedLicensePlate.value = val;
    if (val !== otherOption) {
      submitPlate(newLicensePlate.value);
    }
  }

  watch(selectedLicensePlate, () => {
    isOtherPlate.value = selectedLicensePlate.value === otherOption;
  });
</script>
